import { DashboardRounded } from '@material-ui/icons';
import { Page, useBaseStore } from '@roc/feature-app-core';
import {
  ConversationsDialog,
  useCommunicationRoutesConfig,
  useLoanCommunicationStore,
} from '@roc/feature-communication';
import {
  BorrowerDocumentForms,
  useDocumentStore,
} from '@roc/feature-documents';
import { CustomTaskDialog } from '@roc/feature-general-tasks';
import { LineItemUpload, TodoActions } from '@roc/feature-loan-details';
import { LoanProcess, LoanType, shouldHideTaskUploadTab } from '@roc/feature-utils';
import { FileUploadModal, PDFPreview } from '@roc/ui';
import { observer } from 'mobx-react';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  Redirect,
  Route,
  useHistory,
  useLocation,
  useParams,
} from 'react-router-dom';
import { useBorrowerLoanApplicationsRoutes } from 'libs/feature-borrower-loans/src/hooks/useBorrowerLoanApplicationsRoutes';
import { useBorrowerLoanApplicationsStore } from 'libs/feature-borrower-loans/src/hooks/useBorrowerLoanApplicationsStore';
import { Theme, makeStyles } from '@material-ui/core/styles';
import { BorrowerLoanOverviewBridgeLoanApplications } from '../overview/borrowerLoanOverviewBridgeLoanApplications';
import { BorrowerLoanOverviewTermLoanApplications } from './borrowerLoanOverviewTermLoanApplications';

const useStyles = makeStyles((theme: Theme) => ({
  dialog: {
    '& .MuiDialog-paper': {
      maxWidth: 1000,
    },
  },
}));

enum PlatformUiBorrowerLoanEvent {
  OPEN_TASK = 'platform-ui:borrower-loan:open-task',
  OPEN_TASK_ACTIONS = 'platform-ui:borrower-loan:open-task-actions',
}

// Replaces both BorrowerLoanOverviewBridgeLoanApplications /
// BorrowerLoanOverviewTermLoanApplications and roc's own Loan Summary / To-dos tab bar, the same
// way BorrowerLoanOverviewPlatformUi (borrowerLoanDetailsChildRoutesV3.tsx) replaced the
// funded-loan overviews. The platform view reads the same fields for both loan types, so it no
// longer branches on loanDetails.loanType.
function BorrowerLoanApplicationOverviewPlatformUi({
  loanId,
  initialTab,
  showTodos,
}: {
  loanId: string;
  initialTab?: string;
  showTodos: boolean;
}) {
  return (
    <platform-ui
      id="platform-ui-borrower-loan-application-view"
      component="borrower_loan_application_view"
      data-loan-id={loanId}
      data-tab={initialTab}
      data-show-todos={String(showTodos)}
    />
  );
}

const LoanOverview = observer(({ loanDetails }) => {
  const { globalStore } = useBaseStore();
  const { breadCrumbsStore } = globalStore;
  const { loanId } = useParams<{ loanId: string }>();
  const {
    borrowerLoanApplicationRoutesConfig: loanRoutesConfig,
  } = useBorrowerLoanApplicationsRoutes();

  useEffect(() => {
    breadCrumbsStore.setBreadCrumbItems([
      {
        link: true,
        title: 'Dashboard',
        url: globalStore.routeBaseUrl,
        icon: <DashboardRounded />,
      },
      {
        link: true,
        title: `Loan Application# ${loanId}`,
        url: loanRoutesConfig.loans(loanId).url,
      },
      {
        link: false,
        title: 'Loan Application Summary',
      },
    ]);
  });

  switch (loanDetails?.loanType) {
    case LoanType.RESIDENTIAL_BRIDGE:
      return <BorrowerLoanOverviewBridgeLoanApplications />;
    case LoanType.RESIDENTIAL_TERM:
      return <BorrowerLoanOverviewTermLoanApplications />;
  }
});

export const BorrowerLoanApplicationDetailsChildRoutes = observer(
  ({ isNew = false }) => {
    const { loanId } = useParams<{ loanId: string; drawId: string }>();
    const { globalStore } = useBaseStore();
    const { breadCrumbsStore } = globalStore;
    const {
      borrowerLoanApplicationsStore,
    } = useBorrowerLoanApplicationsStore();
    const { loanDetails } = borrowerLoanApplicationsStore;
    const { documentStore } = useDocumentStore();
    const {
      borrowerLoanApplicationRoutesConfig: loanRoutesConfig,
    } = useBorrowerLoanApplicationsRoutes();
    const loanDetailsChildRoutes = loanRoutesConfig.loans(loanId).children;
    const { communicationRoutesConfig } = useCommunicationRoutesConfig();
    const { push, replace } = useHistory();
    const { pathname, state } = useLocation<{ from?: string }>();
    const { loanCommunicationStore } = useLoanCommunicationStore();
    const classes = useStyles();

    const [uploadTaskDocument, setUploadTaskDocument] = useState<any>(null);
    const [taskActionsDocument, setTaskActionsDocument] = useState<any>(null);
    const [taskActionsPosition, setTaskActionsPosition] = useState({
      clientX: 0,
      clientY: 0,
    });
    const taskActionsRef = useRef<HTMLDivElement>(null);

    // Which tab the web component should open on. The tab bar itself is inside the component, but
    // roc still owns the URLs, so a deep link straight to .../todos has to land on To-dos.
    const platformUiTabRouteUrls: Record<string, string> = {
      'loan-summary': loanDetailsChildRoutes.dashboard.url,
      tasks: loanDetailsChildRoutes.borrowerTodos.url,
    };
    let platformUiInitialTab: string | undefined;
    Object.entries(platformUiTabRouteUrls).forEach(([tab, url]) => {
      if (pathname.indexOf(url) !== -1) {
        platformUiInitialTab = tab;
      }
    });

    useEffect(() => {
      breadCrumbsStore.setBreadCrumbItems([
        {
          link: true,
          title: 'Dashboard',
          url: globalStore.routeBaseUrl,
          icon: <DashboardRounded />,
        },
        {
          link: true,
          title: `Loan Application# ${loanId}`,
          url: loanRoutesConfig.loans(loanId).url,
        },
        {
          link: false,
          title: 'Loan Application Summary',
        },
      ]);
    });

    useEffect(() => {
      if (taskActionsDocument) {
        const button = taskActionsRef.current?.querySelector('button');
        button?.click();
      }
    }, [taskActionsDocument]);

    const notifyTasksChanged = useCallback(() => {
      window.dispatchEvent(
        new CustomEvent('platform-ui:borrower-loan:tasks-changed', {
          detail: { loanId: Number(loanId) },
        })
      );
    }, [loanId]);

    const currentForm = documentStore.documentFormStore?.currentForm;
    const prevCurrentFormRef = useRef(currentForm);
    useEffect(() => {
      if (prevCurrentFormRef.current && !currentForm) {
        notifyTasksChanged();
      }
      prevCurrentFormRef.current = currentForm;
    }, [currentForm, notifyTasksChanged]);

    const onUploadTaskDocument = async (file: File, document: any) => {
      await documentStore.uploadDocument(
        file,
        document,
        LoanProcess.UNDERWRITING,
        null,
        document.drawId
      );
      setUploadTaskDocument(null);
      notifyTasksChanged();
    };

    // The To-dos tab inside the web component only renders the list; opening a to-do and its actions
    // menu still run through roc's document store, exactly as in borrowerLoanDetailsChildRoutesV3.
    useEffect(() => {
      const isSameLoan = (detail: { loanId?: number | string }) =>
        String(detail?.loanId) === String(loanId);

      const handleOpenTask = (e: Event) => {
        const { detail } = e as CustomEvent<{ loanId: number; document: any }>;
        if (!isSameLoan(detail) || !detail?.document) return;
        const document = documentStore.formatDocument(
          detail.document,
          LoanProcess.UNDERWRITING
        );
        if (document.manualTask || (document.isForm && document.canOpenForm)) {
          documentStore.onOpenForm(document);
        } else if (document.canUploadFile) {
          setUploadTaskDocument(document);
        } else if (document.canDownloadFile) {
          const isPDF = document.dropboxPath?.endsWith('.pdf');
          if (isPDF) {
            documentStore.previewDocument(document);
          } else {
            documentStore.downloadDocument(document);
          }
        }
      };

      const handleOpenTaskActions = (e: Event) => {
        const { detail } = e as CustomEvent<{
          loanId: number;
          document: any;
          clientX: number;
          clientY: number;
        }>;
        if (!isSameLoan(detail) || !detail?.document) return;
        const document = documentStore.formatDocument(
          detail.document,
          LoanProcess.UNDERWRITING
        );
        setTaskActionsPosition({
          clientX: detail.clientX,
          clientY: detail.clientY,
        });
        setTaskActionsDocument({ ...document, _key: Date.now() });
      };

      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_TASK,
        handleOpenTask
      );
      window.addEventListener(
        PlatformUiBorrowerLoanEvent.OPEN_TASK_ACTIONS,
        handleOpenTaskActions
      );
      return () => {
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_TASK,
          handleOpenTask
        );
        window.removeEventListener(
          PlatformUiBorrowerLoanEvent.OPEN_TASK_ACTIONS,
          handleOpenTaskActions
        );
      };
    }, [loanId, documentStore]);

    function handleCloseConversationsDialog() {
      push(
        state?.from || loanRoutesConfig.loans(loanId).children.dashboard.url
      );
      loanCommunicationStore.fetchUnreadMessagesCount(loanId);
    }

    function handleConversationSelect(conversationId: string) {
      const path = communicationRoutesConfig
        .loanApplicationConversations(loanId)
        .children.conversation(conversationId).url;
      try {
        replace(path, state);
      } catch (e) {
        push(path, { from: loanDetailsChildRoutes.dashboard.url });
      }
    }

    if (!loanDetails) {
      return null;
    }

    return (
      <>
        <Route
          exact
          path={`${loanRoutesConfig.loans(loanId).path}/`}
          render={() => <Redirect to={loanDetailsChildRoutes.dashboard.url} />}
        />
        <BorrowerLoanApplicationOverviewPlatformUi
          loanId={loanId}
          initialTab={platformUiInitialTab}
          showTodos={!!loanDetails?.toDosFrameworkEnabled}
        />

        <LoanOverview loanDetails={loanDetails} />
        <BorrowerDocumentForms
          loanId={loanId}
          isLoanApplication={true}
          borrowerLoanApplicationsStore={borrowerLoanApplicationsStore}
        />
        <PDFPreview
          open={documentStore.documentPreview != null}
          title={documentStore.documentPreview?.title}
          data={documentStore.documentPreview?.data}
          dataV2={documentStore.documentPreview?.data}
          headers={documentStore.documentPreview?.headers}
          onClose={() => documentStore.closeDocumentPreview()}
        />
        {uploadTaskDocument &&
          (uploadTaskDocument.taskId ? (
            <CustomTaskDialog
              title={uploadTaskDocument.taskName}
              taskId={uploadTaskDocument.taskId}
              open
              dialogContent={
                shouldHideTaskUploadTab(uploadTaskDocument) ? null : (
                  <LineItemUpload document={uploadTaskDocument} expand />
                )
              }
              handleClose={() => {
                setUploadTaskDocument(null);
                notifyTasksChanged();
              }}
            />
          ) : (
            <FileUploadModal
              title={`Upload: ${uploadTaskDocument.taskName}`}
              open
              onClose={() => setUploadTaskDocument(null)}
              onUpload={uploads =>
                onUploadTaskDocument(uploads[0].file, uploadTaskDocument)
              }
            />
          ))}
        {taskActionsDocument && (
          <div
            ref={taskActionsRef}
            style={{
              position: 'fixed',
              left: taskActionsPosition.clientX,
              top: taskActionsPosition.clientY,
              opacity: 0,
              pointerEvents: 'none',
            }}
          >
            <TodoActions
              key={taskActionsDocument._key}
              document={taskActionsDocument}
              loanProcess={LoanProcess.UNDERWRITING}
              onClose={() => {
                setTaskActionsDocument(null);
                notifyTasksChanged();
              }}
            />
          </div>
        )}
        <Route
          path={
            communicationRoutesConfig.loanApplicationConversations(loanId).path
          }
        >
          <Page
            routeDefinition={communicationRoutesConfig.loanApplicationConversations(
              loanId
            )}
          >
            <ConversationsDialog
              open={true}
              loanId={Number(loanId)}
              onClose={handleCloseConversationsDialog}
              communicationStore={loanCommunicationStore}
              onConversationSelect={handleConversationSelect}
              hideSidebar={true}
              allowManageParticipants={true}
              allowNewConversations={false}
              allowAddParticipants={false}
              allowAddInternalParticipants={false}
              allowRemoveParticipants={false}
              allowJoinConversation={false}
              showSnippet={false}
              showEmailAddress={false}
              dialogClass={classes.dialog}
              routePath={
                communicationRoutesConfig
                  .loanApplicationConversations(loanId)
                  .children.conversation().path
              }
            />
          </Page>
        </Route>
      </>
    );
  }
);
